import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';
import { Question } from '@/types/questionnaire';
import { v4 as uuidv4 } from 'uuid';

export interface GeneratedQuestion {
  questionEn: string;
  questionAr: string;
  category?: string;
  options: {
    textEn: string;
    textAr: string;
    score: number;
  }[];
}

export class QuestionnaireAIService {
  private static instance: QuestionnaireAIService;
  
  private constructor() {}
  
  static getInstance(): QuestionnaireAIService {
    if (!QuestionnaireAIService.instance) {
      QuestionnaireAIService.instance = new QuestionnaireAIService();
    }
    return QuestionnaireAIService.instance;
  }

  async generateQuestionsFromInstructions(instructions: string): Promise<GeneratedQuestion[]> {
    const prompt = `You are a healthcare professional creating questionnaire questions for Saudi patients based on specific instructions.

User Instructions: "${instructions}"

Requirements:
- Questions should be culturally appropriate for Saudi patients
- Each question can have 2-6 answer options (you decide based on what makes sense)
- Options should be scored appropriately (lowest score = worst, highest score = best)
- Provide both English and Arabic translations
- Focus on practical, measurable behaviors
- Use respectful, professional language
- Consider Saudi cultural context and lifestyle
- Generate as many questions as needed based on the instructions
- Assign each question to one of these categories: Exercises, Nutrition, Sleep, Stress, Relations, Addictions

Respond with JSON only in this exact format:
{
  "questions": [
    {
      "questionEn": "English question text",
      "questionAr": "النص العربي للسؤال",
      "category": "Exercises",
      "options": [
        {
          "textEn": "Never",
          "textAr": "أبداً",
          "score": 0
        },
        {
          "textEn": "Rarely",
          "textAr": "نادراً",
          "score": 1
        },
        {
          "textEn": "Often",
          "textAr": "غالباً",
          "score": 2
        },
        {
          "textEn": "Always",
          "textAr": "دائماً",
          "score": 3
        }
      ]
    }
  ]
}`;

    try {
      const aiChat = httpsCallable<{ system: string; prompt: string; temperature?: number; maxTokens?: number }, { content: string }>(functions, 'aiChat');
      const result = await aiChat({
        system: 'You are a healthcare professional creating culturally appropriate questionnaire questions for Saudi patients. Always respond with valid JSON only. Be flexible with the number of questions and options based on the user instructions.',
        prompt,
        temperature: 0.7,
        maxTokens: 2000,
      });
      const content = result.data.content?.trim();

      if (content) {
        try {
          const parsed = JSON.parse(content);
          return parsed.questions || [];
        } catch (parseError) {
          console.error('Error parsing AI response:', parseError);
          throw new Error('Invalid JSON response from AI');
        }
      }
      
      throw new Error('Empty response from AI');
    } catch (error) {
      console.error('Error generating questions:', error);
      throw error;
    }
  }

  convertToQuestionFormat(generatedQuestions: GeneratedQuestion[]): Question[] {
    return generatedQuestions.map((gq, index) => ({
      id: uuidv4(),
      question: {
        en: gq.questionEn,
        ar: gq.questionAr
      },
      options: gq.options.map(option => ({
        id: uuidv4(),
        text: {
          en: option.textEn,
          ar: option.textAr
        },
        score: option.score
      })),
      order: index,
      category: (gq as any).category as any || 'Exercises' // fallback to Exercises if no category
    }));
  }

  async generateQuestionsFromCustomInstructions(instructions: string): Promise<Question[]> {
    try {
      console.log('🤖 Generating questions from custom instructions...');
      const generatedQuestions = await this.generateQuestionsFromInstructions(instructions);
      const questions = this.convertToQuestionFormat(generatedQuestions);
      console.log(`✅ Generated ${questions.length} questions from instructions`);
      return questions;
    } catch (error) {
      console.error('❌ Failed to generate questions from instructions:', error);
      throw error;
    }
  }

  async generateAllCategoryQuestions(): Promise<{ [category: string]: Question[] }> {
    const categories = ['Exercises', 'Nutrition', 'Sleep', 'Stress', 'Relations', 'Addictions'];
    const results: { [category: string]: Question[] } = {};
    
    for (const category of categories) {
      try {
        console.log(`🤖 Generating questions for ${category}...`);
        const generatedQuestions = await this.generateQuestionsFromInstructions(`Create 3 questions for ${category} category suitable for Saudi patients`);
        
        // Filter questions for this category and fallback if needed
        const categoryQuestions = generatedQuestions.filter(q => (q as any).category === category);
        if (categoryQuestions.length === 0) {
          // If no questions match the category, assign the first few questions to this category
          const questionsToAssign = generatedQuestions.slice(0, 3).map(q => ({ ...q, category }));
          results[category] = this.convertToQuestionFormat(questionsToAssign);
        } else {
          results[category] = this.convertToQuestionFormat(categoryQuestions);
        }
        
        console.log(`✅ Generated ${results[category].length} questions for ${category}`);
        
        // Add a small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`❌ Failed to generate questions for ${category}:`, error);
        results[category] = [];
      }
    }
    
    return results;
  }
}

export const questionnaireAIService = QuestionnaireAIService.getInstance();
